import React, { useState, useEffect } from "react";

/*************************************************************************
 * NumericInput Component
 * 
 * A reusable input component that handles numeric values.
 * It displays a label and an input field that only accepts numbers.
 * When the user presses "Enter", it triggers the onChange callback with the numeric value.
 * 
 * @param {Object} props
 * @param {string} props.label - The text to display as the label for the input.
 * @param {string} props.id - The unique identifier for the input element.
 * @param {number} props.value - The initial numeric value of the input.
 * @param {Function} props.onChange - Callback function to handle value changes.
 */
const NumericInput = ({ label, id, value, onChange }) => {
    // State to manage the current value of the input field
    const [inputValue, setInputValue] = useState(value);
    const [isValidated, setIsValidated] = useState(true);

    // Reset validation state when external value changes
    useEffect(() => {
        setInputValue(value);
        setIsValidated(true);
    }, [value]);

    /*************************************************************************
     * Handles changes to the input field.
     * Updates the local state with the current input value.
     * 
     * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input.
     */
    const handleChange = (e) => {
        setInputValue(e.target.value);
        setIsValidated(false);
    };

    /*************************************************************************
     * Handles key press events within the input field.
     * Specifically listens for the "Enter" key to trigger the onChange callback.
     * 
     * @param {React.KeyboardEvent<HTMLInputElement>} e - The keyboard event from the input.
     */
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            const newValue = e.target.value;
            // Return null if empty string, otherwise return number
            onChange(newValue === "" ? null : Number(newValue));
            setIsValidated(true);
            e.target.blur(); // Remove focus from the input
        }
    };

    return (
    <div className="flex flex-col w-full">
        {/* Label for the input field */}
        <label
        htmlFor={id}
        className="text-sm font-medium text-gray-700"
        >
        {label}
        </label>

        {/* Numeric input field */}
        <input
        type="number" // Specifies that the input should only accept numbers
        id={id} // Links the input to its label for accessibility
        // Display empty string when value is null
        value={inputValue === null ? '' : inputValue} // Sets the input's value; defaults to empty string if inputValue is null
        onChange={handleChange} // Attaches the change event handler
        onKeyDown={handleKeyDown} // Attaches the key down event handler
        className={`px-3 py-0 border border-gray-300 rounded-md shadow-sm 
            focus:outline-none focus:ring-orange-200 focus:border-orange-200 
            sm:text-sm ${!isValidated ? 'ring-2 ring-red-200' : 'ring-2 ring-green-200'}`}
        />
    </div>
    );
  };
  
  export default NumericInput;
